import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Mic, BarChart3 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A343D] p-14 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#0E4E5A] opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full bg-[#7CD9CC] opacity-10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-10 w-10 object-contain" />
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              PipeGuide
            </span>
          </div>
        </div>

        <div className="space-y-6 relative">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Real-time Sales<br />Qualification Assistant
          </h1>
          <p className="text-white/60 text-lg max-w-md">
            Never miss a qualification question again. Get live AI-powered suggestions during your sales calls.
          </p>

          <div className="grid grid-cols-2 gap-4 pt-6">
            <div className="bg-white/5 backdrop-blur border border-white/10 p-5 rounded-2xl">
              <BarChart3 className="w-8 h-8 text-[#7CD9CC] mb-3" />
              <p className="text-white font-semibold">Live Scoring</p>
              <p className="text-white/50 text-sm mt-0.5">Real-time qualification metrics</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-5 rounded-2xl">
              <Mic className="w-8 h-8 text-[#7CD9CC] mb-3" />
              <p className="text-white font-semibold">Voice Analysis</p>
              <p className="text-white/50 text-sm mt-0.5">AI-powered call insights</p>
            </div>
          </div>
        </div>

        <p className="text-white/40 text-sm relative">
          Built for sales professionals
        </p>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <Card className="w-full max-w-md border-zinc-200 shadow-sm rounded-2xl">
          <CardHeader className="space-y-1 pt-8 px-8">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-8 w-8 object-contain" />
              <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                PipeGuide
              </span>
            </div>
            <CardTitle className="text-3xl tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Welcome back
            </CardTitle>
            <CardDescription>
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2" data-testid="login-error">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-md h-11"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-md h-11"
                  data-testid="login-password-input"
                />
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-brand-primary hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-bold bg-brand-primary hover:bg-brand-hover rounded-xl shadow-md transition-transform hover:-translate-y-0.5"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-zinc-400">or</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl border-zinc-200 hover:bg-zinc-50"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-login-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </Button>

            <div className="mt-6 text-center text-sm">
              <span className="text-zinc-500">Don't have an account? </span>
              <Link to="/register" className="text-brand-primary hover:text-brand-primary font-medium" data-testid="register-link">
                Sign up
              </Link>
            </div>

            <div className="mt-3 text-center text-xs">
              <Link
                to="/demo"
                className="inline-flex items-center gap-1.5 text-brand-primary hover:text-brand-hover font-semibold"
                data-testid="login-see-demo-link"
              >
                ✨ See the interactive demo — no signup needed
              </Link>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-zinc-400">
              <Link to="/terms" className="hover:text-zinc-600 transition-colors" data-testid="footer-terms">Terms</Link>
              <span>·</span>
              <Link to="/privacy" className="hover:text-zinc-600 transition-colors" data-testid="footer-privacy">Privacy</Link>
              <span>·</span>
              <Link to="/security" className="hover:text-zinc-600 transition-colors" data-testid="footer-security">Security</Link>
              <span>·</span>
              <Link to="/sub-processors" className="hover:text-zinc-600 transition-colors" data-testid="footer-subprocessors">Sub-processors</Link>
              <span>·</span>
              <Link to="/data-processing" className="hover:text-zinc-600 transition-colors" data-testid="footer-data">Data Policy</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
